import React, { FC, useCallback } from 'react';

import { Button } from '@eika/button';
import { DetailsListItem } from '@eika/details-list';

import { LoanDetailsOwnersContainer } from '../../components/LoanDetailsOwnersContainer';
import { Content } from '../../components/shared/Content';
import { ErrorCardWrapper } from '../../components/shared/ErrorCardWrapper';
import { useConfig } from '../../defaultConfig';
import { useAccount } from '../../fetchHooks/useAccount';
import { useAuthorizedCustomers } from '../../fetchHooks/useAuthorizedCustomers';
import { useLoanDetails, useLoanInterest, useLoanStakeholders } from '../../fetchHooks/useLoans';
import useAccountId from '../../hooks/queryparams/useAccountId';
import useTranslation from '../../hooks/useTranslation';

import './LoanDetailsOwners.scss';

const LoanDetailsOwners: FC = () => {
    const accountId = useAccountId();
    const translated = useTranslation();
    const {
        featureToggles: { enableLoanStakeholders },
    } = useConfig();
    const { data: account, error: accountError, isLoading: isLoadingAccounts, ...accountState } = useAccount(accountId);
    const interestState = useLoanInterest(accountId);
    const {
        data: loanDetails,
        isLoading: loanDetailsLoading,
        invalidateQuery,
    } = useLoanDetails(accountId, account?.additionalInfo.hasLoanDetails);
    const {
        data: loanStakeholders,
        isLoading: loanStakeholdersLoading,
        error: loanStakeholdersError,
    } = useLoanStakeholders(accountId, enableLoanStakeholders);
    const authorizedCustomersState = useAuthorizedCustomers(accountId, {
        enabled: account?.additionalInfo.authorizedcustomers === true,
    });

    const refresh = useCallback(() => {
        if (accountId) {
            accountState.refetch();
            interestState.refetch();
            authorizedCustomersState.refetch();
            invalidateQuery();
        }
    }, [accountId, accountState, authorizedCustomersState, interestState, invalidateQuery]);

    const isLoading =
        isLoadingAccounts ||
        authorizedCustomersState.isLoading ||
        interestState.isLoading ||
        loanDetailsLoading ||
        loanStakeholdersLoading;

    const error = accountError || interestState.error || loanStakeholdersError;
    if (error) {
        return (
            <Content>
                <ErrorCardWrapper exception={error} defaultError={translated.loanDetails.error}>
                    <Button onClick={refresh}>{translated.tryAgain}</Button>
                </ErrorCardWrapper>
            </Content>
        );
    }

    return (
        <LoanDetailsOwnersContainer
            loanDetails={loanDetails}
            interests={interestState.data}
            accountId={accountId}
            isLoading={isLoading}
            hasAccount={Boolean(account)}
        >
            {authorizedCustomersState.data && authorizedCustomersState.data.length > 0
                ? authorizedCustomersState.data.map((customer) => (
                      <DetailsListItem
                          label={translated.customerRole[customer.role]}
                          value={customer.name}
                          key={`account-details__owner-${customer.name}`}
                      />
                  ))
                : loanStakeholders?.map((loanStakeholder) => (
                      <DetailsListItem
                          label={translated.customerRole[loanStakeholder.role]}
                          value={`${loanStakeholder.firstName} ${loanStakeholder.lastName}`}
                          key={`${loanStakeholder.firstName} ${loanStakeholder.lastName}`}
                      />
                  ))}
        </LoanDetailsOwnersContainer>
    );
};

export default LoanDetailsOwners;
